import React, { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { UserPlus, Loader2 } from "lucide-react";
import { toast } from "sonner";
import BrandStripe from "@/components/BrandStripe";
import Logo from "@/components/Logo";
import { Divider } from "@/components/Divider";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const GOOGLE_AUTH_START = "https://auth.emergentagent.com/";

export default function Signup() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { signup } = useAuth();
  const redirect = params.get("redirect") || "/portal";

  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 8) { setError("Password must be at least 8 characters."); return; }
    setLoading(true);
    try {
      await signup(form);
      toast.success("Account created. Let's finish your application.");
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + redirect;
    window.location.href = `${GOOGLE_AUTH_START}?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen flex flex-col bg-white" data-testid="page-signup">
      <BrandStripe thickness="h-1.5" />
      <main className="flex-1 grid lg:grid-cols-2">
        {/* LEFT — narrative */}
        <section className="flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative overflow-hidden">
          <div className="absolute inset-0 hero-grid opacity-50" />
          <div className="relative w-full max-w-xl">
            <Link to="/"><Logo size={52} /></Link>
            <p className="mt-10 text-xs uppercase tracking-[0.35em] font-bold text-[#00A651]">
              Start Your Application
            </p>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl text-black mt-3 tracking-tight leading-[1.05]">
              Own the <span className="text-[#00A651]">next chapter</span>.
            </h1>
            <Divider className="my-6" />
            <p className="font-sub italic text-lg sm:text-xl text-gray-700">
              Create your secure applicant account. We&apos;ll keep every document, answer,
              and rate quote saved so you can move at your own pace.
            </p>
            <ul className="mt-10 space-y-3 text-gray-700">
              {[
                "Encrypted document vault",
                "AI-verified intake in minutes",
                "Live rate tracking on your dashboard",
                "A human loan originator on standby",
              ].map((t) => (
                <li key={t} className="flex items-start gap-3">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#00A651]" />
                  <span className="font-sub">{t}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* RIGHT — form */}
        <section className="bg-black text-white flex items-center justify-center px-6 sm:px-10 lg:px-16 py-16 relative">
          <div className="absolute top-0 left-0 right-0 h-1 flex">
            <span className="flex-1 bg-[#00A651]" />
            <span className="flex-1 bg-[#E88C2F]" />
            <span className="flex-1 bg-[#ED1C24]" />
            <span className="flex-1 bg-[#F26522]" />
          </div>

          <form onSubmit={submit} className="w-full max-w-md" data-testid="signup-form">
            <div className="w-12 h-12 grid place-items-center bg-[#00A651]/15 text-[#00A651] rounded-sm">
              <UserPlus size={20} />
            </div>
            <p className="mt-6 text-xs uppercase tracking-[0.3em] font-bold text-[#E88C2F]">
              Create Account
            </p>
            <h2 className="font-heading font-bold text-3xl sm:text-4xl mt-2">
              Let&apos;s get you approved
            </h2>
            <p className="font-sub italic text-gray-400 mt-2">
              Takes under a minute. No credit pull required.
            </p>

            <div className="mt-8 space-y-4">
              <Field id="signup-name" label="Full Legal Name" type="text" autoComplete="name"
                     value={form.name} onChange={(v) => setForm({ ...form, name: v })} required minLength={2}
                     testId="signup-name-input" />
              <Field id="signup-email" label="Email" type="email" autoComplete="email"
                     value={form.email} onChange={(v) => setForm({ ...form, email: v })} required
                     testId="signup-email-input" />
              <Field id="signup-phone" label="Phone (optional)" type="tel" autoComplete="tel"
                     value={form.phone} onChange={(v) => setForm({ ...form, phone: v })}
                     testId="signup-phone-input" />
              <Field id="signup-password" label="Password (min 8 characters)" type="password" autoComplete="new-password"
                     value={form.password} onChange={(v) => setForm({ ...form, password: v })} required minLength={8}
                     testId="signup-password-input" />
            </div>

            {error && (
              <p role="alert" data-testid="signup-error"
                 className="mt-4 text-sm text-[#ED1C24] font-bold uppercase tracking-wider">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading}
              data-testid="signup-submit"
              className="mt-8 w-full bg-[#00A651] hover:bg-[#008f45] disabled:opacity-60 text-white font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-sm transition-colors flex items-center justify-center gap-2"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
              {loading ? "Creating account…" : "Create Account"}
            </button>

            <div className="my-6 flex items-center gap-3 text-[10px] uppercase tracking-widest text-gray-500">
              <span className="flex-1 h-px bg-gray-800" />
              or
              <span className="flex-1 h-px bg-gray-800" />
            </div>

            <button
              type="button"
              onClick={googleLogin}
              data-testid="signup-google-button"
              className="w-full bg-white text-black font-bold uppercase tracking-wider text-sm px-6 py-4 rounded-sm hover:bg-gray-100 transition-colors flex items-center justify-center gap-3"
            >
              <GoogleGlyph />
              Sign up with Google
            </button>

            <p className="mt-8 text-sm text-gray-400">
              Already have an account?{" "}
              <Link to={`/login?redirect=${encodeURIComponent(redirect)}`} className="text-[#00A651] font-bold hover:underline" data-testid="signup-to-login">
                Sign in
              </Link>
            </p>
          </form>
        </section>
      </main>
      <BrandStripe thickness="h-1.5" />
    </div>
  );
}

function Field({ id, label, testId, value, onChange, ...rest }) {
  return (
    <div>
      <label htmlFor={id} className="text-[10px] uppercase tracking-widest font-bold text-gray-400">{label}</label>
      <input
        id={id}
        data-testid={testId}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full bg-transparent border-b-2 border-gray-700 focus:border-[#00A651] outline-none py-2 text-white"
        {...rest}
      />
    </div>
  );
}

function GoogleGlyph() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.4-.2-2.1H12v3.9h5.9c-.1 1-.7 2.5-2.1 3.5l-.02.13 3.05 2.36.21.02c1.94-1.79 3.06-4.43 3.06-7.55z"/>
      <path fill="#34A853" d="M12 23c2.76 0 5.08-.9 6.77-2.47l-3.23-2.5c-.87.6-2.03 1.02-3.54 1.02-2.7 0-5-1.78-5.82-4.24l-.12.01-3.17 2.45-.04.11C4.53 20.53 8 23 12 23z"/>
      <path fill="#FBBC05" d="M6.18 14.81a6.9 6.9 0 010-4.62L6.17 10 2.96 7.5l-.11.05a11 11 0 000 8.9l3.33-2.64z"/>
      <path fill="#EA4335" d="M12 5.38c1.92 0 3.21.83 3.95 1.53l2.88-2.81C17.08 2.51 14.76 1.5 12 1.5 8 1.5 4.53 3.97 2.85 7.55l3.33 2.64C7 7.16 9.3 5.38 12 5.38z"/>
    </svg>
  );
}
